'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import { Button } from '@/components';

/**
 * Route-level error boundary. Catches render/data errors in a page segment and
 * renders in place of it (the root layout — header/footer/providers — stays
 * mounted). Must be a client component. For catastrophic errors that take out
 * the layout itself, see global-error.tsx.
 */
export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Surface for local debugging; wire to a real error reporter when one exists.
    console.error('[route error]', error);
  }, [error]);

  return (
    <div className="flex min-h-[70vh] flex-col items-center justify-center bg-background px-4 py-20 text-center">
      <p className="text-6xl font-bold text-brand-100 sm:text-7xl">Oops</p>
      <h1 className="mt-6 text-2xl font-semibold text-text-primary sm:text-3xl">
        Something went wrong
      </h1>
      <p className="mt-3 max-w-md text-text-secondary">
        An unexpected error occurred while loading this page. You can try again, or head back home.
      </p>
      <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row">
        <Button variant="primary" size="lg" onClick={() => reset()}>
          Try again
        </Button>
        <Button asChild variant="secondary" size="lg">
          <Link href="/">Back to home</Link>
        </Button>
      </div>
    </div>
  );
}
